import {
  AlertOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CompassOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FilterOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  PrinterOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  ShopOutlined,
  SolutionOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  TruckOutlined,
  UserOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Steps,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import React, {useMemo, useState} from 'react';

const {Title, Text, Paragraph} = Typography;

const WorkFlow = () => {
  const [activeTab, setActiveTab] = useState('workflows');
  const [selectedRole, setSelectedRole] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkflowModal, setSelectedWorkflowModal] = useState(null);

  // ── Role Access & Responsibilities ─────────────────────────────────────────
  const rolePermissions = [
    {
      role: 'ADMIN',
      badgeColor: 'purple',
      title: 'Administrator Panel',
      description: 'Full access to all system features: inventory management, product catalogs, manufacturing production, staff accounts, billing, attendance, payroll, and settings.',
      allowedModules: ['Dashboard', 'Inventory', 'Products & Production', 'Users & Contacts', 'Bills & Invoicing', 'Attendance', 'Salary & Payroll', 'Profile'],
    },
    {
      role: 'ACCOUNTANT',
      badgeColor: 'cyan',
      title: 'Accountant Panel',
      description: 'Manages sales invoices, customer & vendor accounts, incoming inventory lot stock, billing ledger, wage calculations, and financial PDF exports.',
      allowedModules: ['Dashboard', 'Inventory', 'Products', 'Customers & Vendors', 'Bills & Invoicing', 'Attendance (View)', 'Salary & Payroll', 'Profile'],
    },
    {
      role: 'SUPERVISOR',
      badgeColor: 'gold',
      title: 'Supervisor Panel',
      description: 'Oversees factory floor operations, raw material stock, manufacturing batch production, staff employee directory, daily attendance, and overtime tracking.',
      allowedModules: ['Dashboard', 'Inventory', 'Products & Production', 'Staff Directory', 'Bills & Invoicing', 'Attendance Tracking', 'Profile'],
    },
  ];

  // ── 8 Core Business Workflows ──────────────────────────────────────────────
  const businessWorkflows = [
    {
      id: 'wf_customer',
      title: 'Workflow 1: Add a New Customer or Vendor',
      category: 'Contact Management',
      roles: ['ADMIN', 'ACCOUNTANT', 'SUPERVISOR'],
      icon: <UsergroupAddOutlined className={'text-2xl text-blue-600'} />,
      summary: 'Register new commercial buyers (Customers) or material suppliers (Vendors) into the system contact directory.',
      entryPoint: 'Sidebar → Users → Customers & Vendors Tab → Add Contact',
      steps: [
        {title: 'Step 1: Open Contacts Directory', desc: 'Click Users on the sidebar and select the Customers & Vendors tab.'},
        {title: 'Step 2: Click Add Contact', desc: 'Click the blue Add Contact button at the top right.'},
        {title: 'Step 3: Select Contact Role', desc: 'Choose CUSTOMER (buyer) or VENDOR (supplier) from the role selector.'},
        {title: 'Step 4: Enter Details', desc: 'Fill in Contact Name, Business/Company Name, Email (lowercase), Phone Number (10 digits), GSTIN, and Billing/Shipping Address.'},
        {title: 'Step 5: Click Save Contact', desc: 'The contact is saved and becomes immediately selectable in billing invoices and inventory lot receipts.'},
      ],
      userTip: 'Entering a valid 15-character GSTIN automatically enables tax breakdown calculations for GST billing.',
    },
    {
      id: 'wf_product',
      title: 'Workflow 2: Add a New Product (Raw Material or Final Product)',
      category: 'Product Catalog',
      roles: ['ADMIN', 'ACCOUNTANT', 'SUPERVISOR'],
      icon: <ShopOutlined className={'text-2xl text-emerald-600'} />,
      summary: 'Catalog raw material inputs or finished manufactured goods ready for commercial sale.',
      entryPoint: 'Sidebar → Product → Raw Materials Tab or Final Products Tab',
      steps: [
        {title: 'Step 1: Choose Product Category', desc: 'Go to Product on the sidebar. Open Raw Materials tab for base items, or Final Products tab for finished goods.'},
        {title: 'Step 2: Click Add Button', desc: 'Click Add Product (for Raw Materials) or Add Final Product (for Finished Goods).'},
        {title: 'Step 3: Fill Specifications', desc: 'For Raw: Enter Name, Brand, Category, Unit (Kg, Meters, Pcs), and Low Stock Alert Level. For Final: Enter Name, Brand, HSN Code, Cost Price (₹), Selling Price (₹), Initial Stock, Size, and Color.'},
        {title: 'Step 4: Save Product', desc: 'Click Save Product. The product is added to the catalog and ready for stock receipts or sales.'},
      ],
      userTip: 'Setting a Minimum Alert Quantity ensures the system notifies you on the Dashboard when stock runs low.',
    },
    {
      id: 'wf_inventory',
      title: 'Workflow 3: Manage Inventory & Stock Receipts (Incoming Raw Material)',
      category: 'Supply Chain & Stock',
      roles: ['ADMIN', 'ACCOUNTANT', 'SUPERVISOR'],
      icon: <InboxOutlined className={'text-2xl text-amber-600'} />,
      summary: 'Record raw material supplier shipments, lot numbers, purchase rates, taxes, and print purchase receipts.',
      entryPoint: 'Sidebar → Inventory → Add Lot Button',
      steps: [
        {title: 'Step 1: Open Inventory', desc: 'Click Inventory on the sidebar, then click the Add Lot button at the top right.'},
        {title: 'Step 2: Select Raw Product & Vendor', desc: 'Choose the raw material item received and select the supplying Vendor.'},
        {title: 'Step 3: Enter Shipment Details', desc: 'Enter the supplier Lot Number, received Quantity, and Purchase Price per unit in ₹.'},
        {title: 'Step 4: Tax Information', desc: 'Type the HSN Code and select GST % (e.g. 5%, 12%, 18%) if applicable.'},
        {title: 'Step 5: Click Submit Lot', desc: 'Stock balance increases immediately, the lot is recorded, and an Inventory Lot Invoice is ready for printing.'},
      ],
      userTip: 'Click the Eye Icon on any inventory lot row to view supplier details and print a physical purchase receipt PDF.',
    },
    {
      id: 'wf_production',
      title: 'Workflow 4: Manufacturing Production Batch Processing',
      category: 'Factory Manufacturing',
      roles: ['ADMIN', 'SUPERVISOR'],
      icon: <ThunderboltOutlined className={'text-2xl text-purple-600'} />,
      summary: 'Convert consumed raw material stock into finished salable products via bulk manufacturing batches.',
      entryPoint: 'Sidebar → Product → Production Tab → Create Bulk Batch',
      steps: [
        {title: 'Step 1: Open Production Tab', desc: 'Go to Product on the sidebar and click the Production tab.'},
        {title: 'Step 2: Click Create Bulk Batch', desc: 'Click the Create Bulk Batch button.'},
        {title: 'Step 3: Define Batch Output', desc: 'Enter Batch Number (e.g. BATCH-2026-09), Batch Name, select target Output Final Product, and enter Produced Quantity.'},
        {title: 'Step 4: Add Consumed Raw Materials', desc: 'Under Raw Material Ingredients, select each raw material item used and enter the exact quantity consumed.'},
        {title: 'Step 5: Submit Batch', desc: 'Click Submit Batch. Consumed raw materials are automatically deducted from stock, and produced finished goods are credited to final product inventory.'},
      ],
      userTip: 'Automatic stock balancing occurs instantly upon submission — no manual stock adjustments needed!',
    },
    {
      id: 'wf_billing',
      title: 'Workflow 5: Create a Sales Bill with Transport Delivery Challan',
      category: 'Sales & Invoicing',
      roles: ['ADMIN', 'ACCOUNTANT', 'SUPERVISOR'],
      icon: <FileTextOutlined className={'text-2xl text-cyan-600'} />,
      summary: 'Issue GST or Non-GST sales invoices, select payment modes, add transport logistics details, and print PDFs.',
      entryPoint: 'Sidebar → Bill → Generate Bill Tab',
      steps: [
        {title: 'Step 1: Select Invoice Type & Customer', desc: 'Choose Bill Type (GST or NON_GST), Customer Type, select the Customer name, and choose Bill Category (NORMAL or KUMKUM).'},
        {title: 'Step 2: Add Product Rows', desc: 'Click + Add Product Row, select the finished product, enter Quantity, Unit Price, Discount (if any), and tax rates.'},
        {title: 'Step 3: Choose Payment Method', desc: 'Select Payment Method (CASH, UPI, CARD, CREDIT, CHEQUE, BANK_TRANSFER, ADVANCE) and Payment Mode (OFFLINE or ONLINE).'},
        {title: 'Step 4: Enable Transport Details (Optional)', desc: 'Toggle Enable Transport Details to ON and type Transport Name, Vehicle Number, LR/RR Number, and Destination.'},
        {title: 'Step 5: Generate & Print', desc: 'Click Create & Generate Bill. Click Download Bill PDF for the tax invoice, and click Download Transport Challan for the shipping document.'},
      ],
      userTip: 'Selecting the KUMKUM category automatically applies HSN Code 33049940.',
    },
    {
      id: 'wf_attendance',
      title: 'Workflow 6: Daily Employee Attendance & Overtime Marking',
      category: 'Workforce Management',
      roles: ['ADMIN', 'ACCOUNTANT', 'SUPERVISOR'],
      icon: <ClockCircleOutlined className={'text-2xl text-orange-600'} />,
      summary: 'Log daily staff presence (Present, Absent, Late, Half-Day), record overtime, and inspect monthly calendar reports.',
      entryPoint: 'Sidebar → Attendance',
      steps: [
        {title: 'Step 1: Locate Employee', desc: 'Open Attendance from the sidebar and find the employee in the staff table.'},
        {title: 'Step 2: Click Mark Attendance', desc: 'Click Mark Attendance next to the employee (or click Bulk Mark at top right for multiple workers).'},
        {title: 'Step 3: Select Status & Overtime', desc: 'Select Date (defaults to today), Status (PRESENT, ABSENT, LATE, HALF_DAY), and Overtime (NONE, FIRST_HALF, SECOND_HALF, FULL_DAY, or hours).'},
        {title: 'Step 4: Save Attendance', desc: 'Click Save Attendance. Attendance is locked into the daily log for automatic payroll calculations.'},
      ],
      userTip: 'Click Monthly Report next to any employee to inspect a full calendar grid of check-ins for the month.',
    },
    {
      id: 'wf_salary',
      title: 'Workflow 7: Calculate Monthly Wages & Print Payslips',
      category: 'Payroll & Wages',
      roles: ['ADMIN', 'ACCOUNTANT'],
      icon: <DollarOutlined className={'text-2xl text-emerald-600'} />,
      summary: 'Compute monthly salaries from attendance records, recover salary advances, add bonuses/deductions, and print payslips.',
      entryPoint: 'Sidebar → Salary',
      steps: [
        {title: 'Step 1: Select Payroll Date Range', desc: 'Open Salary and choose the date range for the month (e.g. 01-Sep-2026 to 30-Sep-2026).'},
        {title: 'Step 2: Calculate Salary', desc: 'Click Calculate Salary. The system automatically calculates gross wages based on Present Days, Half Days, Overtime, and Daily Wage Rates.'},
        {title: 'Step 3: Add Adjustments', desc: 'Type any Bonus or Deductions if applicable. Pending salary advances are deducted automatically.'},
        {title: 'Step 4: Process & Download Payslip', desc: 'Click Process Payroll. In the processed list, click Print Payslip next to the employee to download a formatted PDF salary slip.'},
      ],
      userTip: 'Use the Salary Advance button to disburse advances. The system auto-deducts advances during the next payroll calculation.',
    },
    {
      id: 'wf_barcode',
      title: 'Workflow 8: Generate & Download Product Barcode Labels',
      category: 'Packaging & Labels',
      roles: ['ADMIN', 'ACCOUNTANT', 'SUPERVISOR'],
      icon: <BarcodeOutlined className={'text-2xl text-indigo-600'} />,
      summary: 'Preview and download ready-to-print EAN barcode sticker labels for finished commercial products.',
      entryPoint: 'Sidebar → Product → Final Products Tab → Barcode Icon',
      steps: [
        {title: 'Step 1: Open Final Products', desc: 'Go to Product on the sidebar and click the Final Products tab.'},
        {title: 'Step 2: Click Barcode Icon', desc: 'Locate the target product in the table and click the Barcode Icon in the Actions column.'},
        {title: 'Step 3: Preview Barcode Label', desc: 'Inspect the pop-up preview window displaying Product Title, scannable EAN barcode lines, and code number.'},
        {title: 'Step 4: Download PDF', desc: 'Click Download Label PDF. Open the PDF and print it on standard 62 mm × 32 mm barcode sticker rolls.'},
      ],
      userTip: 'Downloaded label PDFs are formatted for standard desktop thermal barcode sticker printers.',
    },
  ];

  // ── Action Icons Reference Table Data ──────────────────────────────────────
  const actionIconData = [
    {key: '1', icon: <EyeOutlined className={'text-blue-600 text-lg'} />, name: 'Eye Icon (View)', description: 'Opens full record details modal, inventory lot purchase receipt preview, or invoice PDF.'},
    {key: '2', icon: <EditOutlined className={'text-amber-600 text-lg'} />, name: 'Edit Icon', description: 'Opens an editing modal window to modify product, user, or invoice information and save changes.'},
    {key: '3', icon: <DeleteOutlined className={'text-red-600 text-lg'} />, name: 'Trash Icon (Delete)', description: 'Opens a confirmation dialog ("Are you sure?") before permanently removing a record.'},
    {key: '4', icon: <PlusOutlined className={'text-emerald-600 text-lg'} />, name: 'Plus Icon (Add)', description: 'Quickly adds a new inventory lot to a product or adds an additional line item row to a bill.'},
    {key: '5', icon: <BarcodeOutlined className={'text-indigo-600 text-lg'} />, name: 'Barcode Icon', description: 'Opens the barcode preview modal for finished products to download 62mm x 32mm sticker PDFs.'},
  ];

  // ── Form Input Guidelines Data ─────────────────────────────────────────────
  const formGuidanceData = [
    {key: '1', form: 'User & Staff Registration', field: 'Role', type: 'Select: EMPLOYEE, SUPERVISOR, ACCOUNTANT, ADMIN', req: 'Yes', rule: 'Must select role. Determines system permissions.'},
    {key: '2', form: 'User & Staff Registration', field: 'Email', type: 'Lowercase text (e.g. user@samadhan.com)', req: 'Yes', rule: 'Must be valid email format typed in all lowercase letters.'},
    {key: '3', form: 'User & Staff Registration', field: 'Password', type: 'Password text', req: 'Conditional', rule: 'Min 6 characters. Required for login roles.'},
    {key: '4', form: 'User & Staff Registration', field: 'Daily Wage Rate', type: 'Number in ₹', req: 'Conditional', rule: 'Must be greater than 0. Required for Employee role.'},
    {key: '5', form: 'Add Inventory Stock Lot', field: 'Raw Product', type: 'Dropdown selection', req: 'Yes', rule: 'Select raw material item from catalog.'},
    {key: '6', form: 'Add Inventory Stock Lot', field: 'Quantity & Price', type: 'Numbers greater than 0', req: 'Yes', rule: 'Physical stock quantity and unit purchase cost in ₹.'},
    {key: '7', form: 'Add Inventory Stock Lot', field: 'HSN Code', type: 'Numeric digits only', req: 'No', rule: 'Must contain numbers only (no letters or spaces).'},
    {key: '8', form: 'Add Inventory Stock Lot', field: 'GST %', type: 'Percentage (0 to 25)', req: 'No', rule: 'Applicable tax rate (e.g. 5%, 12%, 18%).'},
    {key: '9', form: 'Generate Bill (Sales)', field: 'Bill Type', type: 'Selection: GST or NON_GST', req: 'Yes', rule: 'Determines whether tax breakdown applies.'},
    {key: '10', form: 'Generate Bill (Sales)', field: 'Customer Name', type: 'Dropdown selection', req: 'Yes', rule: 'Select buyer from contact directory.'},
    {key: '11', form: 'Generate Bill (Sales)', field: 'Payment Method', type: 'Selection: CASH, UPI, CARD, CREDIT, etc.', req: 'Yes', rule: 'Records financial payment ledger mode.'},
    {key: '12', form: 'Generate Bill (Sales)', field: 'Vehicle Number', type: 'Text (e.g. MH-12-AB-1234)', req: 'Conditional', rule: 'Required if Enable Transport Details is toggled ON.'},
  ];

  // ── Document Generation Quick Reference ───────────────────────────────────
  const documentTypesData = [
    {key: '1', doc: 'Tax Sales Invoice', where: 'Bill → Generate Bill (or Bill History → View PDF)', spec: 'Official GST/Non-GST sales invoice for buyers (A4 Paper).'},
    {key: '2', doc: 'Transport Delivery Challan', where: 'Bill → Generate Bill (when Transport enabled)', spec: 'Goods delivery and vehicle shipping slip for drivers (A4 Paper).'},
    {key: '3', doc: 'Inventory Lot Purchase Receipt', where: 'Inventory → Actions → Click Eye Icon → Print Invoice', spec: 'Supplier lot purchase receipt and raw material tracking record.'},
    {key: '4', doc: 'Product Barcode Sticker Label', where: 'Product → Final Products → Click Barcode Icon', spec: 'Ready-to-print product barcode sticker (62 mm × 32 mm format).'},
    {key: '5', doc: 'Employee Wage Payslip', where: 'Salary → Process Payroll → Click Print Payslip', spec: 'Detailed monthly salary breakdown with attendance, advances, and net pay.'},
  ];

  // ── Troubleshooting Matrix Data ───────────────────────────────────────────
  const troubleshootingData = [
    {key: '1', error: 'Cannot Log In', reason: 'Incorrect email/password, uppercase email, or Caps Lock on.', solution: 'Verify email in lowercase (e.g. user@samadhan.com). Turn off Caps Lock and re-type password carefully. Use Forgot Password? link.'},
    {key: '2', error: 'Purchase price must be greater than 0', reason: 'Zero (0) or negative price entered in stock lot form.', solution: 'Enter a valid positive cost price greater than 0.'},
    {key: '3', error: 'HSN code must contain only numbers', reason: 'Letters, spaces, or symbols entered in HSN field.', solution: 'Remove non-numeric characters and enter digits only (e.g. 33049940).'},
    {key: '4', error: 'Email must be in lowercase', reason: 'Capital letters typed in email input.', solution: 'Re-enter email using lowercase letters only.'},
    {key: '5', error: 'PDF Not Downloading / Printing', reason: 'Browser popup blocker prevented new tab/download.', solution: 'Click blocked popup icon in browser address bar, select "Always allow popups for SAMADHAN ERP", and click Download again.'},
    {key: '6', error: 'Network Error', reason: 'Lost local Wi-Fi or server connection.', solution: 'Check Wi-Fi / internet connection, refresh browser tab (F5), and retry.'},
  ];

  // ── Quick 36 Action Cheat Sheet Data ──────────────────────────────────────
  const quickActionsCheatSheet = [
    {q: '1. How do I log in?', a: 'Open Website → Enter Email → Enter Password → Click Login'},
    {q: '2. How do I recover my password?', a: 'Login Screen → Forgot Password? → Enter Email → Send Code → Verify Code → Enter New Password'},
    {q: '3. How do I add a new customer?', a: 'Users → Customers & Vendors → Add Contact → Select Customer → Enter Info → Save Contact'},
    {q: '4. How do I add a new vendor?', a: 'Users → Customers & Vendors → Add Contact → Select Vendor → Enter Info → Save Contact'},
    {q: '5. How do I add a new employee?', a: 'Users → Employee → Add Employee → Select Role → Enter Wages & Info → Save User'},
    {q: '6. How do I add a raw material?', a: 'Product → Raw Materials → Add Product → Enter Details → Save Product'},
    {q: '7. How do I add a finished product?', a: 'Product → Final Products → Add Final Product → Enter Price/HSN → Save Product'},
    {q: '8. How do I record incoming inventory stock?', a: 'Inventory → Add Lot → Select Raw Product & Vendor → Enter Qty & Price → Submit Lot'},
    {q: '9. How do I view an inventory lot receipt?', a: 'Inventory → Locate Item → Click Eye Icon → Click Print Invoice'},
    {q: '10. How do I create a manufacturing production batch?', a: 'Product → Production → Create Bulk Batch → Select Output Product & Qty → Add Consumed Raw Ingredients → Submit Batch'},
    {q: '11. How do I create a sales invoice?', a: 'Bill → Generate Bill → Select Customer → + Add Product Row → Set Payment Method → Create & Generate Bill'},
    {q: '12. How do I create a GST bill?', a: 'Bill → Generate Bill → Select GST → Select Customer → Add Products & Taxes → Create & Generate Bill'},
    {q: '13. How do I create a Non-GST bill?', a: 'Bill → Generate Bill → Select NON_GST → Select Customer → Add Products → Create & Generate Bill'},
    {q: '14. How do I add transport delivery details to a bill?', a: 'Bill → Generate Bill → Toggle Enable Transport Details ON → Enter Vehicle/Destination → Create & Generate Bill'},
    {q: '15. How do I download an invoice PDF?', a: 'Bill → Generate Bill → Create & Generate Bill → Click Download Bill PDF (or Bill History → View PDF)'},
    {q: '16. How do I download a transport challan?', a: 'Bill → Generate Bill → Enable Transport Details ON → Create & Generate Bill → Click Download Transport Challan'},
    {q: '17. How do I view past invoices?', a: 'Bill → Bill History tab → Search by customer or date → Click View PDF'},
    {q: '18. How do I update invoice payment status?', a: 'Bill → Bill History → Click Update Status → Select PAID, PARTIAL, PENDING, or CANCELLED'},
    {q: '19. How do I mark daily staff attendance?', a: 'Attendance → Find Employee → Click Mark Attendance → Select Status & Overtime → Save Attendance'},
    {q: '20. How do I mark attendance for multiple staff?', a: 'Attendance → Click Bulk Mark → Enter status for workers → Save Attendance'},
    {q: '21. How do I view monthly attendance reports?', a: 'Attendance → Find Employee → Click Monthly Report → View Calendar Grid'},
    {q: '22. How do I calculate monthly wages?', a: 'Salary → Select Date Range → Click Calculate Salary → Review → Process Payroll'},
    {q: '23. How do I record a salary advance?', a: 'Salary → Click Salary Advance → Select Employee → Enter Amount & Reason → Save Advance'},
    {q: '24. How do I print an employee payslip?', a: 'Salary → Process Payroll → Locate Employee → Click Print Payslip (downloads PDF)'},
    {q: '25. How do I generate and print a barcode label?', a: 'Product → Final Products → Click Barcode Icon → Preview → Click Download Label PDF → Print on 62x32mm paper'},
    {q: '26. How do I check low stock warnings?', a: 'Dashboard → Look at Low Stock Warning Cards → Click Check Low in Stock'},
    {q: '27. How do I edit an existing record?', a: 'Locate record in table → Click Edit Icon (✏️) → Update fields → Save Changes'},
    {q: '28. How do I delete a record safely?', a: 'Locate record → Click Trash Icon (🗑️) → Confirm "Yes / Delete" in pop-up'},
    {q: '29. How do I search for a record?', a: 'Type name, brand, category, barcode, or bill number in the Search Box at the top of any table'},
    {q: '30. How do I filter records by date?', a: 'Click Date Range input box on table → Select Start Date & End Date'},
    {q: '31. How do I clear active filters?', a: 'Click Clear or Clear Filters button on table'},
    {q: '32. How do I update my profile details?', a: 'Click Profile Card at bottom of sidebar → Click Edit Profile → Update details → Save Changes'},
    {q: '33. How do I collapse or expand the navigation sidebar?', a: 'Click Sidebar Collapse Button [=] near top left logo'},
    {q: '34. How do I safely log out?', a: 'Click Logout button at bottom of left navigation sidebar (or inside Profile)'},
  ];

  // ── Filtered Business Workflows ───────────────────────────────────────────
  const filteredWorkflows = useMemo(() => {
    return businessWorkflows.filter(wf => {
      const matchesRole = selectedRole === 'ALL' || wf.roles.includes(selectedRole);
      const matchesSearch =
        searchQuery.trim() === '' ||
        wf.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wf.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wf.summary.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesRole && matchesSearch;
    });
  }, [selectedRole, searchQuery]);

  return (
    <div className={'p-6 bg-slate-50 min-h-screen font-sans'}>
      
      {/* Main Header Banner */}
      <Card className={'mb-6 shadow-sm rounded-2xl border-slate-200 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white'}>
        <div className={'flex items-center justify-between flex-wrap gap-4'}>
          <div>
            <div className={'flex items-center gap-3 mb-1'}>
              <CompassOutlined className={'text-3xl text-indigo-400'} />
              <Title level={2} className={'!text-white !mb-0 font-bold'}>
                SAMADHAN ERP — Interactive Website User Guide
              </Title>
            </div>
            <Text className={'text-slate-300 text-sm block'}>
              Enterprise Operations &amp; Training Manual • Version 1.0 — Step-by-step guidance for Administrators, Accountants, Supervisors, and Operations Staff.
            </Text>
          </div>
          <div className={'flex items-center gap-2'}>
            <Tag color={'gold'} className={'px-3 py-1 text-sm font-bold uppercase tracking-wide'}>
              100% Non-Technical End-User Guide
            </Tag>
          </div>
        </div>
      </Card>

      {/* Top Stat Summary Cards */}
      <Row gutter={[16, 16]} className={'mb-6'}>
        <Col xs={24} sm={12} md={6}>
          <Card className={'shadow-sm rounded-xl border-slate-200 hover:shadow-md transition-shadow'}>
            <Statistic
              title={<span className={'text-xs font-semibold text-slate-500 uppercase'}>Core Business Workflows</span>}
              value={8}
              prefix={<ThunderboltOutlined className={'text-indigo-600 mr-2'} />}
              suffix={<span className={'text-xs text-emerald-600 font-medium'}>Step-by-Step</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className={'shadow-sm rounded-xl border-slate-200 hover:shadow-md transition-shadow'}>
            <Statistic
              title={<span className={'text-xs font-semibold text-slate-500 uppercase'}>System Roles Supported</span>}
              value={3}
              prefix={<TeamOutlined className={'text-purple-600 mr-2'} />}
              suffix={<span className={'text-xs text-slate-500 font-medium'}>Admin/Acct/Sup</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className={'shadow-sm rounded-xl border-slate-200 hover:shadow-md transition-shadow'}>
            <Statistic
              title={<span className={'text-xs font-semibold text-slate-500 uppercase'}>Printable PDF Documents</span>}
              value={5}
              prefix={<FilePdfOutlined className={'text-red-500 mr-2'} />}
              suffix={<span className={'text-xs text-slate-500 font-medium'}>Instant Print</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className={'shadow-sm rounded-xl border-slate-200 hover:shadow-md transition-shadow'}>
            <Statistic
              title={<span className={'text-xs font-semibold text-slate-500 uppercase'}>Fast Action Cheat Sheet</span>}
              value={34}
              prefix={<CheckCircleOutlined className={'text-emerald-500 mr-2'} />}
              suffix={<span className={'text-xs text-slate-500 font-medium'}>Quick How-To</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Navigation Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type={'card'}
        className={'bg-white p-5 rounded-2xl shadow-sm border border-slate-200'}
        items={[
          {
            key: 'getting_started',
            label: (
              <span className={'flex items-center gap-2 font-semibold'}>
                <SafetyCertificateOutlined />
                Getting Started &amp; Interface
              </span>
            ),
            children: (
              <div className={'pt-3 space-y-6'}>
                
                {/* Getting Started Guide */}
                <Card title={'1. System Overview & How to Log In'} className={'border-slate-200 rounded-xl shadow-sm'}>
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={12}>
                      <div className={'p-4 rounded-xl bg-slate-50 border border-slate-200 h-full'}>
                        <Text className={'font-bold text-slate-900 block mb-2 text-base'}>🔑 How to Log In Safely</Text>
                        <Timeline
                          items={[
                            {color: 'blue', children: <span>Open website URL in browser (Chrome, Edge, Firefox, Safari).</span>},
                            {color: 'blue', children: <span>Click into <strong>Email</strong> field and enter your lowercase email (e.g. <code>user@samadhan.com</code>).</span>},
                            {color: 'blue', children: <span>Click into <strong>Password</strong> field and type your password.</span>},
                            {color: 'green', children: <span>Click blue <strong>Login</strong> button. Notification displays <i>&quot;Login successful&quot;</i> and redirects to your panel.</span>},
                          ]}
                        />
                      </div>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className={'p-4 rounded-xl bg-slate-50 border border-slate-200 h-full'}>
                        <Text className={'font-bold text-slate-900 block mb-2 text-base'}>🔓 Forgot Password Recovery</Text>
                        <Timeline
                          items={[
                            {color: 'purple', children: <span>Click <strong>Forgot Password?</strong> on login screen.</span>},
                            {color: 'purple', children: <span>Enter registered email and click <strong>Send reset code</strong>.</span>},
                            {color: 'purple', children: <span>Retrieve code from your email inbox, type into <strong>Reset code</strong> field, and click <strong>Verify code</strong>.</span>},
                            {color: 'green', children: <span>Type New Password &amp; Confirm Password, click <strong>Reset password</strong>, then log in.</span>},
                          ]}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card>

                {/* Role Badges Card */}
                <Card title={'2. User Roles & Access Privileges'} className={'border-slate-200 rounded-xl shadow-sm'}>
                  <Row gutter={[16, 16]}>
                    {rolePermissions.map(item => (
                      <Col xs={24} md={8} key={item.role}>
                        <div className={'p-4 rounded-xl bg-slate-50 border border-slate-200 h-full flex flex-col justify-between'}>
                          <div>
                            <div className={'flex items-center justify-between mb-2'}>
                              <Text className={'font-bold text-base text-slate-900'}>{item.title}</Text>
                              <Badge color={item.badgeColor} text={item.role} />
                            </div>
                            <Text className={'text-xs text-slate-600 block mb-3 leading-relaxed'}>{item.description}</Text>
                          </div>
                          <div>
                            <Text className={'text-xs font-bold text-slate-500 block mb-1 uppercase'}>Accessible Sidebar Modules:</Text>
                            <div className={'flex flex-wrap gap-1'}>
                              {item.allowedModules.map(m => (
                                <Tag key={m} color={'blue'} className={'text-xs'}>{m}</Tag>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card>

                {/* Action Icons Legend Table */}
                <Card title={'3. Table Action Icons Legend'} className={'border-slate-200 rounded-xl shadow-sm'}>
                  <Table
                    dataSource={actionIconData}
                    columns={[
                      {title: 'Icon', dataIndex: 'icon', key: 'icon', width: 80, align: 'center'},
                      {title: 'Action Name', dataIndex: 'name', key: 'name', render: text => <Text className={'font-bold text-slate-800'}>{text}</Text>},
                      {title: 'Operational Purpose & Usage', dataIndex: 'description', key: 'description', render: text => <Text className={'text-slate-600 text-sm'}>{text}</Text>},
                    ]}
                    pagination={false}
                    rowKey={'key'}
                    size={'middle'}
                  />
                </Card>
              </div>
            ),
          },
          {
            key: 'workflows',
            label: (
              <span className={'flex items-center gap-2 font-semibold'}>
                <ThunderboltOutlined />
                8 Core Business Workflows ({filteredWorkflows.length})
              </span>
            ),
            children: (
              <div className={'pt-3'}>
                
                {/* Search & Role Filters Bar */}
                <div className={'flex items-center justify-between mb-6 flex-wrap gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200'}>
                  <div className={'flex items-center gap-3'}>
                    <FilterOutlined className={'text-indigo-600'} />
                    <Text className={'font-bold text-slate-700 text-sm'}>Filter by Role Access:</Text>
                    <Select
                      value={selectedRole}
                      onChange={setSelectedRole}
                      style={{width: 160}}
                      options={[
                        {value: 'ALL', label: 'All Roles'},
                        {value: 'ADMIN', label: 'ADMIN'},
                        {value: 'ACCOUNTANT', label: 'ACCOUNTANT'},
                        {value: 'SUPERVISOR', label: 'SUPERVISOR'},
                      ]}
                    />
                  </div>

                  <div className={'flex items-center gap-2'}>
                    <Input
                      placeholder={'Search workflow or keyword…'}
                      prefix={<SearchOutlined className={'text-slate-400'} />}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      allowClear
                      style={{width: 280}}
                    />
                  </div>
                </div>

                {/* Business Workflows Grid */}
                <Row gutter={[16, 16]}>
                  {filteredWorkflows.map(wf => (
                    <Col xs={24} lg={12} key={wf.id}>
                      <Card
                        className={'border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all h-full flex flex-col justify-between'}
                        title={(
                          <div className={'flex items-center gap-3 py-1'}>
                            {wf.icon}
                            <div>
                              <Text className={'font-bold text-base text-slate-900 block'}>{wf.title}</Text>
                              <Text className={'text-xs text-indigo-600 font-semibold'}>{wf.category}</Text>
                            </div>
                          </div>
                        )}
                        extra={(
                          <Button
                            type={'link'}
                            size={'small'}
                            icon={<InfoCircleOutlined />}
                            onClick={() => setSelectedWorkflowModal(wf)}
                          >
                            Step Details
                          </Button>
                        )}
                      >
                        <Text className={'text-sm text-slate-600 block mb-4 leading-relaxed'}>{wf.summary}</Text>

                        <div className={'mb-4 p-2.5 bg-slate-50 rounded-lg border border-slate-200'}>
                          <Text className={'text-xs font-bold text-slate-500 block uppercase mb-1'}>Where to Go (Navigation Path):</Text>
                          <Tag color={'geekblue'} className={'font-medium text-xs'}>{wf.entryPoint}</Tag>
                        </div>

                        <div className={'mb-4'}>
                          <Text className={'text-xs font-bold text-slate-500 block uppercase mb-2'}>Step-by-Step Procedure:</Text>
                          <Steps
                            direction={'vertical'}
                            size={'small'}
                            current={wf.steps.length}
                            items={wf.steps.map(s => ({
                              title: <span className={'text-xs font-bold text-slate-800'}>{s.title}</span>,
                              description: <span className={'text-xs text-slate-600'}>{s.desc}</span>,
                            }))}
                          />
                        </div>

                        <Divider className={'my-3'} />

                        <div className={'flex items-center justify-between flex-wrap gap-2'}>
                          <div className={'flex items-center gap-1'}>
                            <Text className={'text-xs text-slate-400'}>Allowed Roles:</Text>
                            {wf.roles.map(r => (
                              <Tag key={r} color={'purple'} className={'text-xs'}>{r}</Tag>
                            ))}
                          </div>
                          <Button
                            type={'primary'}
                            size={'small'}
                            icon={<ArrowRightOutlined />}
                            onClick={() => setSelectedWorkflowModal(wf)}
                            className={'bg-indigo-600 hover:!bg-indigo-700'}
                          >
                            Inspect Full Workflow Guide
                          </Button>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ),
          },
          {
            key: 'forms_guide',
            label: (
              <span className={'flex items-center gap-2 font-semibold'}>
                <SolutionOutlined />
                Forms &amp; Data Entry Guide
              </span>
            ),
            children: (
              <div className={'pt-3 space-y-6'}>
                <Alert
                  message={'Form Entry Best Practices'}
                  description={'Always double-check required fields before submitting. Fields marked as Required must be completed. Standard casing and numerical formatting rules apply across all forms.'}
                  type={'info'}
                  showIcon
                  className={'rounded-xl'}
                />

                <Card title={'Form Field Requirements & Accepted Formats'} className={'border-slate-200 rounded-xl shadow-sm'}>
                  <Table
                    dataSource={formGuidanceData}
                    columns={[
                      {title: 'Form Name', dataIndex: 'form', key: 'form', render: text => <Text className={'font-bold text-indigo-900'}>{text}</Text>},
                      {title: 'Field Name', dataIndex: 'field', key: 'field', render: text => <Text className={'font-bold text-slate-800'}>{text}</Text>},
                      {title: 'What to Enter / Select', dataIndex: 'type', key: 'type', render: text => <Text className={'text-slate-700 text-sm'}>{text}</Text>},
                      {title: 'Required?', dataIndex: 'req', key: 'req', width: 100, align: 'center', render: req => (
                        <Tag color={req === 'Yes' ? 'red' : req === 'Conditional' ? 'orange' : 'default'} className={'font-bold'}>{req}</Tag>
                      )},
                      {title: 'Format Rule', dataIndex: 'rule', key: 'rule', render: text => <Text className={'text-xs text-slate-600 font-mono'}>{text}</Text>},
                    ]}
                    pagination={{pageSize: 12}}
                    rowKey={'key'}
                    size={'middle'}
                  />
                </Card>
              </div>
            ),
          },
          {
            key: 'documents_pdf',
            label: (
              <span className={'flex items-center gap-2 font-semibold'}>
                <FilePdfOutlined />
                Printable PDF Documents
              </span>
            ),
            children: (
              <div className={'pt-3 space-y-6'}>
                <Card title={'Official Downloadable PDF Documents & Print Specifications'} className={'border-slate-200 rounded-xl shadow-sm'}>
                  <Table
                    dataSource={documentTypesData}
                    columns={[
                      {title: 'Document Type', dataIndex: 'doc', key: 'doc', render: text => <Text className={'font-bold text-slate-900 text-base'}>{text}</Text>},
                      {title: 'Where to Generate / Print', dataIndex: 'where', key: 'where', render: text => <Tag color={'geekblue'} className={'font-medium'}>{text}</Tag>},
                      {title: 'Document Usage & Print Specifications', dataIndex: 'spec', key: 'spec', render: text => <Text className={'text-slate-700 text-sm'}>{text}</Text>},
                    ]}
                    pagination={false}
                    rowKey={'key'}
                    size={'middle'}
                  />
                </Card>

                <Alert
                  message={'Where Are Downloaded PDFs Saved?'}
                  description={'All generated PDFs download directly into your web browser\'s default Downloads folder (Press Ctrl+J or Cmd+Option+L in your browser to view downloads).'}
                  type={'success'}
                  showIcon
                  className={'rounded-xl'}
                />
              </div>
            ),
          },
          {
            key: 'troubleshooting',
            label: (
              <span className={'flex items-center gap-2 font-semibold'}>
                <AlertOutlined />
                Troubleshooting &amp; Common Errors
              </span>
            ),
            children: (
              <div className={'pt-3 space-y-6'}>
                <Card title={'Common User Error Messages & Resolutions'} className={'border-slate-200 rounded-xl shadow-sm'}>
                  <Table
                    dataSource={troubleshootingData}
                    columns={[
                      {title: 'Problem / Error Message', dataIndex: 'error', key: 'error', render: text => <Text className={'font-bold text-red-600'}>{text}</Text>},
                      {title: 'Possible Reason', dataIndex: 'reason', key: 'reason', render: text => <Text className={'text-slate-600 text-sm'}>{text}</Text>},
                      {title: 'What the User Should Do (Resolution)', dataIndex: 'solution', key: 'solution', render: text => <Text className={'text-slate-800 text-sm font-medium'}>{text}</Text>},
                    ]}
                    pagination={false}
                    rowKey={'key'}
                    size={'middle'}
                  />
                </Card>
              </div>
            ),
          },
          {
            key: 'quick_cheat_sheet',
            label: (
              <span className={'flex items-center gap-2 font-semibold'}>
                <QuestionCircleOutlined />
                Quick &quot;How Do I?&quot; Guide (34 Fast Actions)
              </span>
            ),
            children: (
              <div className={'pt-3'}>
                <Card title={'34 Fast Action Cheat Sheets'} className={'border-slate-200 rounded-xl shadow-sm'}>
                  <Row gutter={[16, 12]}>
                    {quickActionsCheatSheet.map(item => (
                      <Col xs={24} md={12} key={item.q}>
                        <div className={'p-3 rounded-lg bg-slate-50 border border-slate-200 h-full flex flex-col justify-between'}>
                          <Text className={'font-bold text-slate-900 text-sm block mb-1'}>{item.q}</Text>
                          <Text className={'text-xs text-indigo-700 font-semibold block'}>{item.a}</Text>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </div>
            ),
          },
        ]}
      />

      {/* Workflow Step Details Modal */}
      {selectedWorkflowModal && (
        <Modal
          title={(
            <div className={'flex items-center gap-3 py-1'}>
              {selectedWorkflowModal.icon}
              <div>
                <Text className={'font-bold text-lg text-slate-900 block'}>{selectedWorkflowModal.title}</Text>
                <Text className={'text-xs text-indigo-600 font-semibold'}>{selectedWorkflowModal.category}</Text>
              </div>
            </div>
          )}
          open={!!selectedWorkflowModal}
          onCancel={() => setSelectedWorkflowModal(null)}
          footer={[
            <Button key={'close'} onClick={() => setSelectedWorkflowModal(null)} type={'primary'}>
              Close Guide
            </Button>,
          ]}
          width={720}
        >
          <div className={'mt-4 space-y-5 text-sm'}>
            <div className={'p-3.5 bg-indigo-50 rounded-xl border border-indigo-100'}>
              <Text className={'text-xs text-indigo-700 font-bold uppercase block mb-1'}>Summary &amp; Operational Purpose</Text>
              <Text className={'text-sm text-indigo-950 font-medium block leading-relaxed'}>{selectedWorkflowModal.summary}</Text>
            </div>

            <div className={'p-3 bg-slate-50 rounded-xl border border-slate-200'}>
              <Text className={'text-xs font-bold text-slate-500 uppercase block mb-1'}>Where to Go (Navigation Path):</Text>
              <Tag color={'geekblue'} className={'font-medium text-xs'}>{selectedWorkflowModal.entryPoint}</Tag>
            </div>

            <div>
              <Text className={'font-bold text-xs text-slate-600 uppercase block mb-3'}>Step-by-Step Execution Sequence:</Text>
              <Steps
                direction={'vertical'}
                current={selectedWorkflowModal.steps.length}
                items={selectedWorkflowModal.steps.map(s => ({
                  title: <span className={'text-sm font-bold text-slate-900'}>{s.title}</span>,
                  description: <span className={'text-xs text-slate-600 leading-relaxed block mt-0.5'}>{s.desc}</span>,
                }))}
              />
            </div>

            <Alert
              message={'User Best Practice Tip'}
              description={selectedWorkflowModal.userTip}
              type={'success'}
              showIcon
              className={'rounded-xl'}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default WorkFlow;
